/* Plan unosa jednog listića: skala listića -> UTC dani, spajanje sa postojećim danima, konflikti, payload za snimanje. */
import { listicUIntervale, poDanima, toAppDate, naive, apiNaive, fmtNaive, localToUtc, DAY_MS } from './engine/vreme';
import { addDays, toDateStr } from '../../utils/dates';
import { activityLabel } from '../../utils/activities';

/** Date aplikacije (zidno vreme) -> naivni ms */
const uNaive = (v) => { const x = new Date(v); return naive(x, x.getHours() * 60 + x.getMinutes()); };

export const IZVOR_LABEL = { KARTICA: 'kartica', LISTIC: 'listić', RUCNO: 'ručni unos' };

const MIN = 60000;
const BREAK = 'break/rest';
/** minutni niz dana -> detalji (spojeni po tipu, izvoru i postojećem redu iz kog potiču — izvorOd = id reda u bazi) */
const uDetalje = (dan, tip, izv, od = []) => {
    const out = [];
    let st = 0;
    for (let i = 1; i <= 1440; i++) {
        if (i === 1440 || tip[i] !== tip[st] || izv[i] !== izv[st] || (od[i] || null) !== (od[st] || null)) {
            out.push({ activityType: tip[st], od: dan + st * MIN, do: dan + i * MIN, izvor: izv[st], izvorOd: od[st] || null });
            st = i;
        }
    }
    return out;
};

/** red sa kartice / tahografa vozila (osim odmora i nepoznatog) se ne menja (isto proverava baza, dbo.izvorAktivnostiDana) */
const zakljucanRed = (x) => (x.izvor === 'KARTICA' || x.izvor === 'VOZILO') && x.activityType !== BREAK && x.activityType !== 'unknown';

/** postojeći redovi dana (Date aplikacije, sa izvorom iz baze) -> naivni ms */
const stariRedovi = (postojeci) => (postojeci || []).map((x) => {
    const s = new Date(x.startTime), e = new Date(x.endTime);
    return {
        activityType: x.activityType, od: naive(s, s.getHours() * 60 + s.getMinutes()), do: naive(e, e.getHours() * 60 + e.getMinutes()),
        id: x.id || null, izvor: x.izvor || null, listicId: x.listicId || null, listic: x.listic || null, zakljucan: zakljucanRed(x),
    };
}).filter((x) => x.do > x.od);

/** aktivnosti koje su VEĆ u bazi (sve osim odmora i nepoznatog) -> segmenti trake listića (UTC minut dana, kao prikaz trake), sa
    izvorom — operater na listiću i traci vidi osenčeno šta postoji (kartica / drugi listić / ručno); samo prikaz, ne unosi se sa listićem.
    Ceo UTC dan izabranog datuma bez obzira na početak aktivnosti (ranije samo 24 h od početka — deo dana pre početka se nije video),
    i deo perioda listića u susednom danu. */
export function postojeceNaTraci(item, plan) {
    if (!plan || !plan.utc || !item.provera || !item.datumOd) return [];
    const dan = naive(item.datumOd);
    const prozori = [[dan, dan + DAY_MS], [plan.utc.od, Math.min(plan.utc.do, dan)], [Math.max(plan.utc.od, dan + DAY_MS), plan.utc.do]];
    const out = [];
    (item.provera.postojeci || []).flatMap((d) => stariRedovi(d.activityDetails))
        .filter((x) => x.activityType !== BREAK && x.activityType !== 'unknown').forEach((x) => prozori.forEach(([p0, p1]) => {
            const od = Math.max(x.od, p0), kraj = Math.min(x.do, p1);
            /* traka je 0–24 h: deo preko UTC ponoći ide na početak trake */
            for (let a = od; a < kraj;) {
                const granica = Math.min(kraj, Math.floor(a / DAY_MS) * DAY_MS + DAY_MS);
                const s = Math.round((a % DAY_MS) / MIN);
                out.push({ start: s, end: s + Math.round((granica - a) / MIN), type: x.activityType, izvor: x.izvor, listicId: x.listicId, listic: x.listic, zakljucan: x.zakljucan });
                a = granica;
            }
        }));
    return out;
}

/** red plana koji potiče od postojećeg reda nosi i njegov izvor iz baze (prikaz: kartica / listić X / ručno; zaključan) */
const saIzvorom = (detalji, stari) => {
    const po = new Map(stari.filter((x) => x.id).map((x) => [x.id, x]));
    return detalji.map((r) => {
        const s = r.izvorOd ? po.get(r.izvorOd) : null;
        return s ? { ...r, baza: s.izvor, listicId: s.listicId, listic: s.listic, zakljucan: s.zakljucan } : r;
    });
};

/**
 * Spajanje dana listića sa postojećim danom (minut po minut).
 *   nacin 'zameni'  u periodu listića važe aktivnosti sa listića (postojeće van perioda ostaje)
 *         'zadrzi'  postojeće aktivnosti ostaju; listić popunjava samo odmor / nepoznato / prazno u svom periodu
 *   ponisti   id-jevi prethodnih listića koje ovaj listić zamenjuje CELE: njihovi redovi postaju odmor (i van perioda ovog listića)
 * Kartica (vožnja, rad, raspoloživost) se nikad ne menja; odmor i nepoznato sa kartice listić popunjava u svom periodu (od početka aktivnosti).
 * izvor reda: 'postoji' (prethodni unos, izvorOd = id reda), 'novo' (sa listića), 'ponisten' (red zamenjenog listića -> odmor),
 *             null (dopunski odmor)
 * konflikt  = postojeće aktivnosti osim odmora i nepoznatog koje se preklapaju sa periodom listića
 * kartica   = zaključani redovi kartice (ne odmor / nepoznato) u periodu listića (listić preko kartice je nemoguć -> izbacivanje)
 * prethodni = redovi prethodnih listića (osim odmora) u periodu ovog listića — "Zameni listićem" briše te listiće cele
 */
export function spojiDanNacin(d, postojeci, nacin = 'zameni', ponisti = new Set()) {
    const tip = new Array(1440).fill(BREAK), izv = new Array(1440).fill(null), od = new Array(1440).fill(null), zak = new Array(1440).fill(false);
    const stari = stariRedovi(postojeci);
    const brisan = (x) => !!x.listicId && ponisti.has(x.listicId);
    const mi = (ms) => Math.max(0, Math.min(1440, Math.round((ms - d.dan) / MIN)));
    stari.forEach((x) => {
        const b0 = brisan(x);
        for (let i = mi(x.od); i < mi(x.do); i++) { tip[i] = b0 ? BREAK : x.activityType; izv[i] = b0 ? 'ponisten' : 'postoji'; od[i] = b0 ? null : x.id; zak[i] = x.zakljucan; }
    });
    const a = mi(d.od), b = mi(d.do);
    const slobodno = (i) => tip[i] === BREAK || tip[i] === 'unknown';
    if (nacin === 'zameni') for (let i = a; i < b; i++) if (!zak[i]) { tip[i] = BREAK; izv[i] = 'novo'; od[i] = null; }
    d.intervali.forEach((x) => {
        for (let i = mi(x.od); i < mi(x.do); i++) {
            if (zak[i]) continue; // eslint-disable-line no-continue
            if (nacin === 'zadrzi' && izv[i] === 'postoji' && !slobodno(i)) continue; // eslint-disable-line no-continue
            tip[i] = x.type; izv[i] = 'novo'; od[i] = null;
        }
    });
    if (nacin === 'zadrzi') for (let i = a; i < b; i++) if (izv[i] !== 'novo' && slobodno(i) && !zak[i]) { tip[i] = BREAK; izv[i] = 'novo'; od[i] = null; }
    const preklapa = (x) => x.od < d.do && x.do > d.od;
    const zivi = stari.filter((x) => !brisan(x));
    const konflikt = zivi.filter((x) => x.activityType !== BREAK && x.activityType !== 'unknown' && preklapa(x));
    const kartica = zivi.filter((x) => x.zakljucan && preklapa(x));
    const prethodni = zivi.filter((x) => x.izvor === 'LISTIC' && x.listicId && x.activityType !== BREAK && x.activityType !== 'unknown' && preklapa(x));
    return { detalji: saIzvorom(uDetalje(d.dan, tip, izv, od), stari), konflikt, kartica, prethodni, postojeciDetalji: stari };
}

/** naivni UTC ms -> lokalno (Beograd) zidno vreme */
const utcULokal = (u) => (localToUtc(u + 7200000) === u ? u + 7200000 : u + 3600000);

/** aktivnosti listića (sve osim odmora) u minutima skale: izmenjene od strane operatera ili prepoznate
    (ono što je već u bazi se samo prikazuje — postojeceNaTraci — i ne unosi se sa listićem) */
export const aktivnostiOf = (item) => (item.aktivnosti
    || (item.result ? item.result.segments.filter((s) => s.type && s.type !== BREAK).map((s) => ({ start: s.start, end: s.end, type: s.type })) : []));

/**
 * Izmena tabele dana (operater) -> patch stavke, da listić, traka i tabela uvek pokazuju isto:
 *   u periodu listića tog dana izmena se upisuje u aktivnosti skale listića (kao izmena na slici);
 *   van perioda listića (postojeće aktivnosti dana) čuva se kao ručna izmena dana, samo ako se razlikuje od izračunatog.
 * @param d      dan plana ({ dan, od, do }, naivni UTC ms)
 * @param lista  detalji iz tabele [{ activityType, startTime, endTime, izvor }] (Date aplikacije)
 */
export function izmenaDana(item, d, lista, srednja = 'availability') {
    const base = naive(item.datumOd);
    const redovi = lista.map((x) => ({ type: x.activityType, od: uNaive(x.startTime), do: uNaive(x.endTime), izvor: x.izvor }));
    const naMinutu = (t) => redovi.find((x) => x.od <= t && t < x.do);
    /* skala listića: minut -> tip (null = odmor) */
    const m = new Array(1440).fill(null);
    aktivnostiOf(item).forEach((a) => { for (let i = Math.max(0, a.start); i < Math.min(1440, a.end); i++) m[i] = a.type; });
    for (let t = d.od; t < d.do; t += MIN) {
        const r = naMinutu(t);
        const tip = r ? r.type : BREAK;
        const sk = ((Math.round((utcULokal(t) - base) / MIN) % 1440) + 1440) % 1440;
        /* srednja traka listića je 'availability' i kad znači ostali rad (mapirano) */
        m[sk] = tip === BREAK ? null : srednja !== 'availability' && tip === srednja ? 'availability' : tip;
    }
    const aktivnosti = [];
    for (let i = 0; i < 1440; i++) {
        const z = aktivnosti[aktivnosti.length - 1];
        if (!m[i]) continue; // eslint-disable-line no-continue
        if (z && z.type === m[i] && z.end === i) z.end = i + 1; else aktivnosti.push({ start: i, end: i + 1, type: m[i] });
    }
    /* van perioda listića: poređenje sa izračunatim danom (bez ručne izmene) */
    const rr = { ...(item.rucno || {}) };
    delete rr[d.dan];
    const novi = napraviPlan({ ...item, aktivnosti, rucno: null }, srednja);
    const izr = novi && novi.dani.find((x) => x.dan === d.dan);
    const van = [];
    let razlika = false;
    for (let t = d.dan; t < d.dan + DAY_MS; t += MIN) {
        if (t >= d.od && t < d.do) continue; // eslint-disable-line no-continue
        /* obrisan red van perioda listića = odmor (dan uvek ima 24 h; ranije je ostajala rupa i dan nije mogao da se unese) */
        const r = naMinutu(t) || { type: BREAK };
        const z = izr && izr.detalji.find((x) => x.od <= t && t < x.do);
        const isto = r && z && r.type === z.activityType;
        if (!isto) razlika = true;
        const izvor = isto ? z.izvor : 'rucno';
        const izvorOd = isto ? z.izvorOd || null : null;
        const p = van[van.length - 1];
        if (p && p.activityType === r.type && p.izvor === izvor && p.izvorOd === izvorOd && p.do === t) p.do = t + MIN;
        else van.push({ activityType: r.type, od: t, do: t + MIN, izvor, izvorOd });
    }
    if (razlika) rr[d.dan] = van.map((x) => ({ activityType: x.activityType, startTime: toAppDate(x.od), endTime: toAppDate(x.do), izvor: x.izvor, izvorOd: x.izvorOd }));
    return { aktivnosti, rucno: rr };
}

/** listić bez ijednog zapisa (ceo krug "nije uložen") */
const ceoPrazan = (r) => !!r && !!r.segments && r.segments.every((s) => !s.type);
/** ceo prazan listić, početak nije pomeran: podrazumevano uložen u 00:00 UTC i ceo dan je odmor (00–24 UTC) — ranije bez perioda,
    pa provera nije ni krenula ("Provera…" zauvek, Bogdanović 005); vlasnik: "ako ne nađe nijednu aktivnost, start i kraj 0 UTC" */
const podrazumevanPrazan = (item) => ceoPrazan(item.result) && !!item.datumOd && item.ulaganje === item.result.ulaganje;
/** početak aktivnosti (minut skale listića) — za ceo prazan listić 00:00 UTC tog dana (lokalno 01:00 zimi / 02:00 leti) */
export const ulaganjeOf = (item) => (podrazumevanPrazan(item) ? utcULokal(naive(item.datumOd)) / MIN % 1440 : item.ulaganje);

/** skala bez aktivnosti: odmor i "listić nije uložen" (null) iz prepoznavanja / pomerenog početka */
function osnovaSkale(item) {
    const m = new Array(1440).fill('break/rest');
    const r = item.result;
    if (podrazumevanPrazan(item)) { /* ceo dan odmor */ }
    /* operater je pomerio početak aktivnosti: "listić nije uložen" traje od vađenja do novog početka */
    else if (r.prazno && item.ulaganje != null && item.ulaganje !== r.ulaganje) {
        for (let i = r.vadjenje % 1440; i !== item.ulaganje % 1440; i = (i + 1) % 1440) m[i] = null;
    } else r.segments.forEach((s) => { if (!s.type) for (let i = s.start; i < s.end; i++) m[i] = null; });
    return m;
}

/** pun krug skale: odmor, "listić nije uložen" (iz prepoznavanja) i preko toga aktivnosti (kasnija u spisku ima prednost) */
export function segmentiOf(item) {
    if (!item.result) return [];
    const m = osnovaSkale(item);
    aktivnostiOf(item).forEach((a) => { for (let i = Math.max(0, a.start); i < Math.min(1440, a.end); i++) m[i] = a.type; });
    const out = [];
    let st = 0;
    for (let i = 1; i <= 1440; i++) if (i === 1440 || m[i] !== m[st]) { out.push({ start: st, end: i, type: m[st] }); st = i; }
    return out;
}

/** segmenti sa zamenom značenja srednje trake (raspoloživost / ostali rad) */
const mapirano = (segments, srednja) => segments.map((s) => (s.type === 'availability' && srednja !== 'availability' ? { ...s, type: srednja } : s));

/**
 * @param item     stavka bulk-a (result, datumOd, ulaganje, km, provera)
 * @param srednja  'availability' | 'work'
 * @returns null (nedostaje datum/rezultat) ili
 *   { lokalno, utc, datumDo, dani: [{ dan, od, do, detalji, konflikt, izvor, postoji }], konflikt: bool }
 */
export function napraviPlan(item, srednja = 'availability') {
    if (!item || !item.result || !item.datumOd) return null;
    const L = listicUIntervale(mapirano(segmentiOf(item), srednja), item.datumOd, ulaganjeOf(item));
    if (!L.lokalno) return null;
    const postojeci = (item.provera && item.provera.postojeci) || [];
    const izvori = (item.provera && item.provera.izvori) || {};
    const delovi = poDanima(L.intervali, L.utc);
    /* listić je po lokalnom vremenu, a dani su UTC: listić koji je ceo dan u tahografu (00–24 h Beograd) zahvata i 1–2 h
       susednog UTC dana (pomeraj Beograda). Takav krajnji deo sa SAMO odmorom se podrazumevano ne unosi — taj dan dopunjuje
       odmorom njegov listić (praznine su odmor), a ranije je bulk za svaki listić nudio dva dana. Operater ga može uključiti. */
    const samoOdmorKraj = (d) => delovi.length > 1 && d.do - d.od <= 2 * 3600000 && d.intervali.every((x) => x.type === BREAK);
    const postojiZa = (d) => postojeci.find((x) => naive(new Date(x.activityDate)) === d.dan);
    const nacinZa = (d) => (item.naciniDana && item.naciniDana[d.dan]) || (samoOdmorKraj(d) ? 'preskoci' : 'zameni');
    /* "Zameni listićem": prethodni listići čija se aktivnost preklapa sa ovim listićem brišu se CELI (svi njihovi dani -> odmor) */
    const prethodni = new Map();
    delovi.forEach((d) => {
        if (nacinZa(d) !== 'zameni') return;
        const p = postojiZa(d);
        spojiDanNacin(d, p ? p.activityDetails : null, 'zameni').prethodni.forEach((x) => {
            const z = prethodni.get(x.listicId) || { id: x.listicId, listic: x.listic, od: x.od, do: x.do };
            prethodni.set(x.listicId, { ...z, od: Math.min(z.od, x.od), do: Math.max(z.do, x.do) });
        });
    });
    const ponisti = new Set(prethodni.keys());
    const dani = delovi.map((d) => {
        const p = postojiZa(d);
        const nacin = nacinZa(d);
        const s = spojiDanNacin(d, p ? p.activityDetails : null, nacin === 'preskoci' ? 'zadrzi' : nacin, ponisti);
        const out = { ...d, ...s, nacin, preskocen: nacin === 'preskoci', postoji: !!p, izvor: izvori[d.dan] || null };
        /* ručna izmena dana VAN perioda listića (u periodu listića izmena iz tabele ide na sam listić — izmenaDana) */
        const rucno = item.rucno && item.rucno[d.dan];
        if (rucno) {
            const tip = new Array(1440).fill(undefined), izv = new Array(1440).fill(null), od = new Array(1440).fill(null);
            const mi = (ms) => Math.max(0, Math.min(1440, Math.round((ms - d.dan) / MIN)));
            const a = mi(d.od), b = mi(d.do);
            s.detalji.forEach((x) => { for (let i = Math.max(a, mi(x.od)); i < Math.min(b, mi(x.do)); i++) { tip[i] = x.activityType; izv[i] = x.izvor; od[i] = x.izvorOd; } });
            rucno.forEach((x) => {
                for (let i = mi(uNaive(x.startTime)); i < mi(uNaive(x.endTime)); i++) {
                    if (i < a || i >= b) { tip[i] = x.activityType; izv[i] = x.izvor === undefined ? 'rucno' : x.izvor; od[i] = x.izvorOd || null; }
                }
            });
            out.rucno = true;
            out.detalji = saIzvorom(uDetalje(d.dan, tip, izv, od).filter((x) => x.activityType !== undefined), s.postojeciDetalji);
            out.suma = out.detalji.reduce((z, x) => z + (x.do - x.od), 0);
        }
        return out;
    });
    /* pređeni put: razlika kilometraže deli se po danima srazmerno vožnji */
    const km = item.kmOd != null && item.kmDo != null && item.kmDo >= item.kmOd ? item.kmDo - item.kmOd : null;
    if (km != null) {
        const voznja = dani.map((d) => d.intervali.filter((x) => x.type === 'driving').reduce((a, x) => a + x.do - x.od, 0));
        const ukupno = voznja.reduce((a, b) => a + b, 0);
        let ostatak = km;
        dani.forEach((d, i) => {
            d.km = i === dani.length - 1 ? ostatak : ukupno ? Math.round((km * voznja[i]) / ukupno) : 0;
            ostatak -= d.km;
        });
    }
    return {
        lokalno: L.lokalno,
        utc: L.utc,
        datumDo: addDays(item.datumOd, Math.floor((L.lokalno.do - 1 - naive(item.datumOd)) / DAY_MS)),
        dani,
        konflikt: dani.some((d) => d.konflikt.length && !d.preskocen),
        /* listić preko kartice je nemoguć: takav listić ide na izbacivanje */
        kartica: dani.some((d) => d.kartica.length && !d.preskocen),
        /* prethodni listići koje "Zameni listićem" briše cele [{id, listic, od, do}] */
        ponisti: [...prethodni.values()],
        /* zamena = bar jedan dan sa konfliktom u kome listić menja postojeće */
        zamena: dani.some((d) => d.konflikt.length && d.nacin === 'zameni'),
        neispravno: dani.some((d) => d.rucno && d.suma !== DAY_MS),
        prazno: dani.every((d) => d.preskocen),
    };
}

/** tekst postojećih aktivnosti (za napomenu u evidenciji i prikaz) */
export const opisKonflikta = (plan) => plan.dani.filter((d) => d.konflikt.length).map((d) => `${fmtNaive(d.dan, false)}: ${d.konflikt
    .map((k) => `${activityLabel(k.activityType)} ${fmtNaive(k.od).slice(11)}–${fmtNaive(k.do).slice(11)}`).join(', ')}`).join('; ');

const prviIzvor = (plan) => (plan.kartica ? 'KARTICA' : (plan.dani.find((d) => d.konflikt.length) || {}).izvor || null);

/** tekst aktivnosti sa kartice koje se ne slažu sa listićem */
export const opisKartice = (plan) => plan.dani.filter((d) => d.kartica.length && !d.preskocen).map((d) => `${fmtNaive(d.dan, false)}: ${d.kartica
    .map((k) => `${activityLabel(k.activityType)} ${fmtNaive(k.od).slice(11)}–${fmtNaive(k.do).slice(11)}`).join(', ')}`).join('; ');

/** zapis listića za evidenciju */
export const zapisListica = (item, plan) => ({
    fileName: item.name,
    sha256: item.sha256,
    pocetakLokalno: apiNaive(plan.lokalno.od),
    krajLokalno: apiNaive(plan.lokalno.do),
    pocetakUtc: apiNaive(plan.utc.od),
    krajUtc: apiNaive(plan.utc.do),
    datumOd: toDateStr(item.datumOd),
    datumDo: toDateStr(plan.datumDo),
    izvor: plan.konflikt || plan.kartica ? prviIzvor(plan) : null,
    napomena: plan.kartica ? `Preklapa se sa karticom — ${opisKartice(plan)}`.slice(0, 1000) : plan.konflikt ? opisKonflikta(plan).slice(0, 1000) : null,
    pouzdanost: Math.round(item.result.pouzdanost * 10000) / 100,
    kmPocetak: item.kmOd,
    kmKraj: item.kmDo,
});

/** dani za snimanje (pun dan, 24 h); po redu odakle je (baza upisuje izvor): novo = ovaj listić (i dopunski odmor dana),
    izvorOd = postojeći red koji ostaje, ostalo (ručna izmena, red zamenjenog listića) = ručno */
export const daniZaSnimanje = (plan) => plan.dani.filter((d) => !d.preskocen).map((d) => ({
    activityDate: toAppDate(d.dan),
    traversedDistance: d.km || 0,
    activityDetails: d.detalji.map((x) => ({
        activityType: x.activityType, startTime: toAppDate(x.od), endTime: toAppDate(x.do),
        novo: x.izvor === 'novo' || x.izvor === null, ...(x.izvorOd ? { izvorOd: x.izvorOd } : {}),
    })),
}));

/**
 * Predlog datuma ulaganja bez čitanja rukopisa: listić nastavlja prethodni listić istog vozača (redosled u spisku).
 * Ako je uložen posle sata vađenja prethodnog -> isti dan kao vađenje, inače sledeći dan.
 * @returns { datum: Date, od: ime prethodnog listića } ili null
 */
export function predlogDatuma(items, i, planovi) {
    const item = items[i];
    if (!item || !item.driver || !item.result) return null;
    for (let j = i - 1; j >= 0; j--) {
        const prev = items[j];
        if (!prev.driver || prev.driver.id !== item.driver.id) continue; // eslint-disable-line no-continue
        if (!prev.datumOd) return null; // prethodni listić istog vozača još nema datum — niz čeka
        const pp = planovi(prev);
        /* listić bez zapisa (prazan, "PAUZA") nema vreme vađenja: sledeći je dan posle njegovog datuma */
        const prazan = !pp || !prev.result || !prev.result.segments || prev.result.segments.every((s) => !s.type);
        if (prazan) return { datum: toAppDate(naive(prev.datumOd) + DAY_MS), od: prev.name };
        const vadjenje = pp.lokalno.do;
        const dan = Math.floor(vadjenje / DAY_MS) * DAY_MS;
        const minut = (vadjenje - dan) / 60000;
        /* ovaj listić je prazan (nema vreme ulaganja): stavljen je kad je prethodni izvađen — isti dan */
        const ovajPrazan = !item.result.segments || item.result.segments.every((s) => !s.type);
        if (ovajPrazan) return { datum: toAppDate(dan), od: prev.name };
        return { datum: toAppDate(item.ulaganje >= minut ? dan : dan + DAY_MS), od: prev.name };
    }
    return null;
}

/**
 * Pomeranje segmenata skale za delta minuta (prikaz u UTC: delta = -pomeraj; nazad: +pomeraj).
 * Segment koji pređe ponoć deli se na dva; susedni istog tipa se spajaju. Radi i za pune segmente (odmor, "nema").
 */
export function pomeri(lista, delta) {
    if (!delta) return lista.map((a) => ({ ...a }));
    const out = [];
    lista.forEach((a) => {
        let s = a.start + delta, e = a.end + delta;
        if (e <= 0) { s += 1440; e += 1440; } else if (s >= 1440) { s -= 1440; e -= 1440; }
        if (s < 0) { out.push({ ...a, start: s + 1440, end: 1440 }); out.push({ ...a, start: 0, end: e }); }
        else if (e > 1440) { out.push({ ...a, start: s, end: 1440 }); out.push({ ...a, start: 0, end: e - 1440 }); }
        else out.push({ ...a, start: s, end: e });
    });
    out.sort((x, y) => x.start - y.start);
    const spojeno = [];
    out.forEach((a) => {
        const z = spojeno[spojeno.length - 1];
        if (z && z.type === a.type && z.end === a.start) z.end = a.end; else spojeno.push({ ...a });
    });
    return spojeno;
}
